import React, { useState, useRef, ChangeEvent, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { registroService, type DadosCadastro, type RespostaRegistro } from '../services/api/registroService';
import trackingService from '../services/trackingService';

export default function Cadastro() {
  const navigate = useNavigate();

  // Rastrear início do cadastro
  useEffect(() => {
    trackingService.trackStartRegistration('individual');
  }, []);
  
  // Estados para dados pessoais - ETAPA 1
  const [nome, setNome] = useState('');
  const [email, setEmail] = useState('');
  const [telefone, setTelefone] = useState('');
  const [crp, setCrp] = useState('');
  
  // Estados para dados da empresa/profissional - ETAPA 2
  const [nomeEmpresa, setNomeEmpresa] = useState('');
  const [nomeFantasia, setNomeFantasia] = useState('');
  const [emailEmpresa, setEmailEmpresa] = useState('');
  const [telefoneEmpresa, setTelefoneEmpresa] = useState('');
  const [cnpj, setCnpj] = useState('');
  const [endereco, setEndereco] = useState('');
  const [cep, setCep] = useState('');
  const [cidade, setCidade] = useState('');
  const [estado, setEstado] = useState('');

  // Estados para dados de login - NOVA ETAPA 3
  const [empresaLogin, setEmpresaLogin] = useState('');
  const [usuario, setUsuario] = useState('');
  const [senha, setSenha] = useState('');
  const [confirmaSenha, setConfirmaSenha] = useState('');

  // Estado para foto de perfil
  const [fotoPerfil, setFotoPerfil] = useState<string | null>(null);
  const [fotoNome, setFotoNome] = useState('');
  
  // Estados para o processo
  const [etapa, setEtapa] = useState(1);
  const [erros, setErros] = useState<Record<string, string>>({});
  const [carregando, setCarregando] = useState(false);
  const [mensagemSucesso, setMensagemSucesso] = useState('');
  
  // Referência para o input de arquivo
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Validar formulário - etapa 1 (dados pessoais)
  const validarEtapa1 = (): boolean => {
    const novosErros: Record<string, string> = {};
    
    if (!nome.trim()) novosErros.nome = 'Nome é obrigatório';
    if (!email.trim()) novosErros.email = 'E-mail é obrigatório';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) 
      novosErros.email = 'E-mail inválido';
    
    setErros(novosErros);
    return Object.keys(novosErros).length === 0;
  };
  
  // Validar formulário - etapa 2 (dados profissionais)
  const validarEtapa2 = (): boolean => {
    const novosErros: Record<string, string> = {};
    
    if (!nomeEmpresa.trim()) novosErros.nomeEmpresa = 'Nome profissional/clínica é obrigatório';
    
    // CNPJ é opcional para psicólogos individuais
    if (cnpj.trim() && !validarCNPJ(cnpj)) 
      novosErros.cnpj = 'CNPJ inválido';
      
    setErros(novosErros);
    return Object.keys(novosErros).length === 0;
  };

  // Validar formulário - etapa 3 (dados de login)
  const validarEtapa3 = (): boolean => {
    const novosErros: Record<string, string> = {};
    
    if (!empresaLogin.trim()) novosErros.empresaLogin = 'Nome da empresa é obrigatório';
    else if (empresaLogin.length < 3) novosErros.empresaLogin = 'Nome da empresa deve ter pelo menos 3 caracteres';
      
    if (!usuario.trim()) novosErros.usuario = 'Nome de usuário é obrigatório';
    if (!senha) novosErros.senha = 'Senha é obrigatória';
    else if (senha.length < 6) novosErros.senha = 'Senha deve ter pelo menos 6 caracteres';
    if (senha !== confirmaSenha) novosErros.confirmaSenha = 'Senhas não conferem';
    
    setErros(novosErros);
    return Object.keys(novosErros).length === 0;
  };
  
  // Validação de CNPJ (simplificada)
  const validarCNPJ = (cnpj: string): boolean => {
    // Remove caracteres não numéricos
    cnpj = cnpj.replace(/[^\d]/g, '');
    
    // Verifica se tem 14 dígitos
    if (cnpj.length !== 14) return false;
    
    // Verifica se todos os dígitos são iguais (caso inválido)
    if (/^(\d)\1+$/.test(cnpj)) return false;
    
    // Para uma validação completa, implementaríamos o algoritmo de verificação dos dígitos
    // Mas para simplificar, consideramos válido se não for uma sequência repetida
    return true;
  };
  
  // Função para avançar para a próxima etapa
  const avancarEtapa = () => {
    if (etapa === 1 && validarEtapa1()) {
      setEtapa(2);
      setErros({});
    } else if (etapa === 2 && validarEtapa2()) {
      setEtapa(3);
      setErros({});
    }
  };
  
  // Função para retornar à etapa anterior
  const voltarEtapa = () => {
    setEtapa(etapa - 1);
    setErros({});
  };
  
  // Função para ler arquivo como base64
  const lerComoBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = error => reject(error);
      reader.readAsDataURL(file);
    });
  };

  // Handler para mudança de foto
  const handleFotoChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Verificar o tamanho do arquivo (limitar a 2MB)
      if (file.size > 2 * 1024 * 1024) {
        setErros(prev => ({ ...prev, foto: 'A foto deve ter no máximo 2MB' }));
        return;
      }

      // Verificar o tipo do arquivo
      if (!file.type.startsWith('image/')) {
        setErros(prev => ({ ...prev, foto: 'Apenas arquivos de imagem são permitidos' }));
        return;
      }

      try {
        const base64 = await lerComoBase64(file);
        setFotoPerfil(base64);
        setFotoNome(file.name);
        
        // Limpar erro se houver
        const novosErros = { ...erros };
        delete novosErros.foto;
        setErros(novosErros);
      } catch (error) {
        console.error('Erro ao converter imagem:', error);
        setErros(prev => ({ ...prev, foto: 'Erro ao processar a imagem' }));
      }
    }
  };

  // Handler para submissão do formulário - SIMPLIFICADO
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validarEtapa3()) return;
    
    try {
      setCarregando(true);
      
      // CORREÇÃO: Dados simplificados com valores padrão
      const dadosCadastro: DadosCadastro = {
        usuario: {
          nome,
          email,
          usuario,
          senha,
          telefone: telefone || undefined,
          fotoPerfil: fotoPerfil || undefined,
          crp: crp || null,
          ativo: true,
          cargo: 'admin'
        },
        empresa: {
          nome: nomeEmpresa,
          empresa_login: empresaLogin,
          nome_fantasia: nomeFantasia || null,
          email: emailEmpresa || email,
          telefone: telefoneEmpresa || telefone,
          cnpj: cnpj.replace(/[^\d]/g, '') || null,
          endereco: endereco || undefined,
          cep: cep.replace(/[^\d]/g, '') || null,
          cidade: cidade || null,
          estado: estado || null,
          ativo: true
        }
      };
      
      const resultado: RespostaRegistro = await registroService.registrarNovoUsuario(dadosCadastro);
      
      if (resultado.success) {
        // Rastrear cadastro completo
        trackingService.trackRegistrationComplete({
          type: 'individual',
          planName: 'Individual Trial',
          trialDays: 30
        });
        
        setMensagemSucesso('Cadastro realizado com sucesso! Você já pode fazer login.');
        
        // Redirecionar para login após 3 segundos
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else {
        throw new Error(resultado.message || 'Erro no cadastro');
      }
      
    } catch (error) {
      if (error instanceof Error) {
        setErros({
          ...erros,
          form: error.message || 'Erro ao realizar cadastro. Tente novamente.'
        });
      } else {
        setErros({
          ...erros,
          form: 'Ocorreu um erro inesperado. Tente novamente mais tarde.'
        });
      }
    } finally {
      setCarregando(false);
    }
  };

  return (
    <div className="register-page">
      <div className="register-box">
        <div className="card">
          <div className="card-header text-center">
            <img src="/img/logo-principal.png" alt="Logo" style={{ height: '60px', marginBottom: '10px' }} />
            <h1 className="h3">Criar Nova Conta</h1>
          </div>
          <div className="card-body register-card-body">
            {mensagemSucesso ? (
              <div className="alert alert-success text-center">
                <h4>Cadastro realizado com sucesso!</h4>
                <p>{mensagemSucesso}</p>
                <Link to="/login" className="btn btn-primary">
                  Fazer Login
                </Link>
              </div>
            ) : (
              <>
                {/* Indicador de progresso */}
                <div className="progress mb-3" style={{ height: '6px' }}>
                  <div 
                    className="progress-bar" 
                    style={{ width: `${(etapa / 3) * 100}%` }}
                  ></div>
                </div>
                <p className="text-center text-muted">Etapa {etapa} de 3</p>

                {Object.keys(erros).length > 0 && (
                  <div className="alert alert-danger">
                    <ul className="mb-0">
                      {Object.values(erros).map((erro, index) => (
                        <li key={index}>{erro}</li>
                      ))}
                    </ul>
                  </div>
                )}

                <form onSubmit={(e) => { 
                  e.preventDefault(); 
                  if (etapa < 3) {
                    avancarEtapa();
                  } else {
                    handleSubmit(e);
                  }
                }}>
                  {etapa === 1 && (
                    <>
                      <h5 className="mb-3">Dados Pessoais</h5>
                      
                      <div className="mb-3">
                        <div className="input-group">
                          <input
                            type="text"
                            className={`form-control ${erros.nome ? 'is-invalid' : ''}`}
                            placeholder="Nome completo"
                            value={nome}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => setNome(e.target.value)}
                            required
                          />
                          <div className="input-group-append">
                            <div className="input-group-text">
                              <i className="fas fa-user"></i>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="mb-3">
                        <div className="input-group">
                          <input
                            type="email"
                            className={`form-control ${erros.email ? 'is-invalid' : ''}`}
                            placeholder="E-mail"
                            value={email}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
                            required
                          />
                          <div className="input-group-append">
                            <div className="input-group-text">
                              <i className="fas fa-envelope"></i>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="mb-3">
                        <div className="input-group">
                          <input
                            type="tel"
                            className="form-control"
                            placeholder="Telefone/WhatsApp (opcional)"
                            value={telefone}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => setTelefone(e.target.value)}
                          />
                          <div className="input-group-append">
                            <div className="input-group-text">
                              <i className="fas fa-phone"></i>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Campo para foto de perfil */}
                      <div className="mb-3">
                        <label className="form-label" htmlFor="cadastroFotoPerfil">Foto de Perfil (opcional)</label>
                        <input
                          ref={fileInputRef}
                          id="cadastroFotoPerfil"
                          type="file"
                          className={`form-control ${erros.foto ? 'is-invalid' : ''}`}
                          accept="image/*"
                          onChange={handleFotoChange}
                        />
                        {erros.foto && <div className="invalid-feedback">{erros.foto}</div>}
                        {fotoPerfil && (
                          <div className="mt-2">
                            <img 
                              src={fotoPerfil} 
                              alt="Preview" 
                              style={{ width: '50px', height: '50px', objectFit: 'cover', borderRadius: '50%' }}
                            />
                            <small className="text-muted ml-2">{fotoNome}</small>
                          </div>
                        )}
                      </div>

                      <button type="submit" className="btn btn-primary btn-block">
                        Continuar <i className="fas fa-arrow-right ml-2"></i>
                      </button>
                    </>
                  )}

                  {etapa === 2 && (
                    <>
                      <h5 className="mb-3">Dados Profissionais</h5>
                      
                      <div className="mb-3">
                        <div className="input-group">
                          <input
                            type="text"
                            className={`form-control ${erros.nomeEmpresa ? 'is-invalid' : ''}`}
                            placeholder="Nome da clínica/consultório"
                            value={nomeEmpresa}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => setNomeEmpresa(e.target.value)}
                            required
                          />
                          <div className="input-group-append">
                            <div className="input-group-text">
                              <i className="fas fa-building"></i>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="mb-3">
                        <div className="input-group">
                          <input
                            type="text"
                            className={`form-control ${erros.cnpj ? 'is-invalid' : ''}`}
                            placeholder="CNPJ (opcional)"
                            value={cnpj}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => setCnpj(e.target.value)}
                          />
                          <div className="input-group-append">
                            <div className="input-group-text">
                              <i className="fas fa-id-card"></i>
                            </div>
                          </div>
                        </div>
                        <small className="form-text text-muted">
                          Deixe em branco se for psicólogo autônomo
                        </small>
                      </div>

                      <div className="mb-3">
                        <div className="input-group">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Endereço completo"
                            value={endereco}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => setEndereco(e.target.value)}
                          />
                          <div className="input-group-append">
                            <div className="input-group-text">
                              <i className="fas fa-map-marker-alt"></i>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="row">
                        <div className="col-md-4 mb-3">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="CEP"
                            value={cep}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => setCep(e.target.value)}
                          />
                        </div>
                        <div className="col-md-4 mb-3">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Cidade"
                            value={cidade}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => setCidade(e.target.value)}
                          />
                        </div>
                        <div className="col-md-4 mb-3">
                          <select
                            className="form-control"
                            value={estado}
                            onChange={(e: ChangeEvent<HTMLSelectElement>) => setEstado(e.target.value)}
                          >
                            <option value="">UF</option>
                            <option value="AC">AC</option>
                            <option value="AL">AL</option>
                            <option value="AP">AP</option>
                            <option value="AM">AM</option>
                            <option value="BA">BA</option>
                            <option value="CE">CE</option>
                            <option value="DF">DF</option>
                            <option value="ES">ES</option>
                            <option value="GO">GO</option>
                            <option value="MA">MA</option>
                            <option value="MT">MT</option>
                            <option value="MS">MS</option>
                            <option value="MG">MG</option>
                            <option value="PA">PA</option>
                            <option value="PB">PB</option>
                            <option value="PR">PR</option>
                            <option value="PE">PE</option>
                            <option value="PI">PI</option>
                            <option value="RJ">RJ</option>
                            <option value="RN">RN</option>
                            <option value="RS">RS</option>
                            <option value="RO">RO</option>
                            <option value="RR">RR</option>
                            <option value="SC">SC</option>
                            <option value="SP">SP</option>
                            <option value="SE">SE</option>
                            <option value="TO">TO</option>
                          </select>
                        </div>
                      </div>

                      {/* CORREÇÃO: Adicionar campo de telefone que estava faltando */}
                      <div className="mb-3">
                        <div className="input-group">
                          <input
                            type="tel"
                            className="form-control"
                            placeholder="Telefone/WhatsApp"
                            value={telefone}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => setTelefone(e.target.value)}
                          />
                          <div className="input-group-append">
                            <div className="input-group-text">
                              <i className="fas fa-phone"></i>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* CORREÇÃO: Adicionar campo de CRP que estava faltando */}
                      <div className="mb-3">
                        <div className="input-group">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="CRP - Registro profissional (opcional)"
                            value={crp}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => setCrp(e.target.value)}
                          />
                          <div className="input-group-append">
                            <div className="input-group-text">
                              <i className="fas fa-id-badge"></i>
                            </div>
                          </div>
                        </div>
                        <small className="form-text text-muted">
                          Ex: CRP 01/12345 (Conselho Regional de Psicologia)
                        </small>
                      </div>

                      {/* CORREÇÃO: Adicionar campo de nome fantasia que estava faltando */}
                      <div className="mb-3">
                        <div className="input-group">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Nome fantasia (opcional)"
                            value={nomeFantasia}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => setNomeFantasia(e.target.value)}
                          />
                          <div className="input-group-append">
                            <div className="input-group-text">
                              <i className="fas fa-store"></i>
                            </div>
                          </div>
                        </div>
                        <small className="form-text text-muted">
                          Nome comercial da clínica (se diferente do nome oficial)
                        </small>
                      </div>

                      {/* CORREÇÃO: Adicionar campos de contato da empresa que estavam faltando */}
                      <div className="row">
                        <div className="col-md-6 mb-3">
                          <div className="input-group">
                            <input
                              type="email"
                              className="form-control"
                              placeholder="E-mail da empresa (opcional)"
                              value={emailEmpresa}
                              onChange={(e: ChangeEvent<HTMLInputElement>) => setEmailEmpresa(e.target.value)}
                            />
                            <div className="input-group-append">
                              <div className="input-group-text">
                                <i className="fas fa-envelope"></i>
                              </div>
                            </div>
                          </div>
                          <small className="form-text text-muted">
                            Se diferente do seu e-mail pessoal
                          </small>
                        </div>
                        <div className="col-md-6 mb-3">
                          <div className="input-group">
                            <input
                              type="tel"
                              className="form-control"
                              placeholder="Telefone da empresa (opcional)"
                              value={telefoneEmpresa}
                              onChange={(e: ChangeEvent<HTMLInputElement>) => setTelefoneEmpresa(e.target.value)}
                            />
                            <div className="input-group-append">
                              <div className="input-group-text">
                                <i className="fas fa-phone"></i>
                              </div>
                            </div>
                          </div>
                          <small className="form-text text-muted">
                            Se diferente do seu telefone pessoal
                          </small>
                        </div>
                      </div>

                      <div className="row">
                        <div className="col-6">
                          <button type="button" className="btn btn-secondary btn-block" onClick={voltarEtapa}>
                            <i className="fas fa-arrow-left mr-2"></i> Voltar
                          </button>
                        </div>
                        <div className="col-6">
                          <button type="submit" className="btn btn-primary btn-block">
                            Continuar <i className="fas fa-arrow-right ml-2"></i>
                          </button>
                        </div>
                      </div>
                    </>
                  )}

                  {/* ETAPA 3: Dados de Login */}
                  {etapa === 3 && (
                    <>
                      <h5 className="mb-3">Dados de Acesso ao Sistema</h5>
                      <p className="text-muted small mb-4">
                        <i className="fas fa-info-circle mr-1"></i>
                        Defina suas credenciais para acessar o sistema
                      </p>

                      <div className="mb-3">
                        <label className="form-label small text-muted mb-1" htmlFor="cadastroEmpresaLogin">
                          <i className="fas fa-building me-1"></i>
                          Nome da Empresa/Clínica (para login)
                        </label>
                        <div className="input-group">
                          <input
                            id="cadastroEmpresaLogin"
                            type="text"
                            className={`form-control ${erros.empresaLogin ? 'is-invalid' : ''}`}
                            placeholder="Ex: Clinica Ana Silva"
                            value={empresaLogin}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => setEmpresaLogin(e.target.value)}
                            required
                          />
                          <div className="input-group-append">
                            <div className="input-group-text">
                              <i className="fas fa-building"></i>
                            </div>
                          </div>
                        </div>
                        <small className="form-text text-muted">
                          💡 Nome simples para identificar sua empresa no login. Não precisa ser a razão social completa.
                        </small>
                      </div>

                      <div className="mb-3">
                        <label className="form-label small text-muted mb-1" htmlFor="cadastroUsuarioLogin">
                          <i className="fas fa-user me-1"></i>
                          Nome de Usuário (para login)
                        </label>
                        <div className="input-group">
                          <input
                            id="cadastroUsuarioLogin"
                            type="text"
                            className={`form-control ${erros.usuario ? 'is-invalid' : ''}`}
                            placeholder="Ex: ana.silva"
                            value={usuario}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => setUsuario(e.target.value)}
                            required
                          />
                          <div className="input-group-append">
                            <div className="input-group-text">
                              <i className="fas fa-at"></i>
                            </div>
                          </div>
                        </div>
                        <small className="form-text text-muted">
                          💡 Este será seu nome de usuário para fazer login no sistema
                        </small>
                      </div>

                      <div className="mb-3">
                        <label className="form-label small text-muted mb-1" htmlFor="cadastroSenha">
                          <i className="fas fa-lock me-1"></i>
                          Senha
                        </label>
                        <div className="input-group">
                          <input
                            id="cadastroSenha"
                            type="password"
                            className={`form-control ${erros.senha ? 'is-invalid' : ''}`}
                            placeholder="Mínimo 6 caracteres"
                            value={senha}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => setSenha(e.target.value)}
                            required
                          />
                          <div className="input-group-append">
                            <div className="input-group-text">
                              <i className="fas fa-lock"></i>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="mb-3">
                        <label className="form-label small text-muted mb-1" htmlFor="cadastroConfirmaSenha">
                          <i className="fas fa-lock me-1"></i>
                          Confirmar Senha
                        </label>
                        <div className="input-group">
                          <input
                            id="cadastroConfirmaSenha"
                            type="password"
                            className={`form-control ${erros.confirmaSenha ? 'is-invalid' : ''}`}
                            placeholder="Digite a senha novamente"
                            value={confirmaSenha}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => setConfirmaSenha(e.target.value)}
                            required
                          />
                          <div className="input-group-append">
                            <div className="input-group-text">
                              <i className="fas fa-lock"></i>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="alert alert-info">
                        <i className="fas fa-lightbulb mr-2"></i>
                        <strong>Importante:</strong> Guarde bem essas informações! Você precisará delas para acessar o sistema.
                      </div>

                      <div className="row">
                        <div className="col-6">
                          <button type="button" className="btn btn-secondary btn-block" onClick={voltarEtapa}>
                            <i className="fas fa-arrow-left mr-2"></i> Voltar
                          </button>
                        </div>
                        <div className="col-6">
                          <button 
                            type="submit" 
                            className="btn btn-primary btn-block" 
                            disabled={carregando}
                          >
                            {carregando ? (
                              <>
                                <span className="spinner-border spinner-border-sm mr-2"></span>
                                Criando...
                              </>
                            ) : (
                              <>
                                <i className="fas fa-user-plus mr-2"></i>
                                Criar Conta
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </form>

                <div className="text-center mt-3">
                  <p>
                    Já tem uma conta?{' '}
                    <Link to="/login" className="text-primary fw-bold">
                      Faça login
                    </Link>
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
